import {
  type AssetTypeType,
  AttributeDataType,
  type AttributeDataTypeType,
  type AttributeValueType,
  FileUploadedSchema,
  type FileUploadedType,
  type FormSchemaNormalQuestionType,
  type FormSchemaQuestionAnswerType,
  FormSchemaQuestionType,
  type FormSchemaSectionType,
  type FormSchemaType,
  type FormSubmissionAnswersByQuestionMapType,
  type FormSubmissionAnswersBySectionMapType,
  type FormSubmissionAnswersInFormType,
  type FormSubmissionAnswerType,
  type FormSubmissionType,
} from "@validereinc/domain";
import { DateTimeValueSchemas } from "@validereinc/utilities";
import isValidDate from "date-fns/isValid";
import parseISO from "date-fns/parseISO";
import { get, isEqual, isObject, isPlainObject, set } from "es-toolkit/compat";
import {
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormReturn,
  type UseFormSetValue,
} from "react-hook-form";

export const $in = ({
  value,
  comparator,
}: {
  value: FormSchemaQuestionAnswerType;
  comparator: AttributeValueType[];
}) =>
  Array.isArray(value)
    ? comparator.some(
        (comparatorValue) =>
          (value as number[]).includes(comparatorValue as number) ||
          (value as string[]).includes(comparatorValue as string)
      )
    : comparator.includes(value);

export const $eq = ({
  value,
  comparator,
}: {
  value: FormSchemaQuestionAnswerType | Date;
  comparator: FormSchemaQuestionAnswerType | Date;
}) => {
  switch (typeof value) {
    case "boolean":
      return value === comparator;
    case "string":
      return value === String(comparator);
    case "number":
      return value === Number(comparator);
    case "object": {
      if (Array.isArray(value)) {
        return value.every((v, idx) => {
          if (Array.isArray(comparator)) {
            return v === comparator[idx];
          } else {
            return v === comparator;
          }
        });
      } else if (value instanceof Date) {
        if (comparator instanceof Date) {
          return value.getTime() === comparator.getTime();
        } else if (typeof comparator === "string") {
          return value.getTime() === parseISO(comparator).getTime();
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
    default:
      return false;
  }
};

export const $lt = ({
  value,
  comparator,
}: {
  value: number | string;
  comparator: number;
}) => {
  if (typeof value === "string" && value === "") {
    return false;
  }
  return Number(value) < comparator;
};

export const $lte = ({
  value,
  comparator,
}: {
  value: number | string;
  comparator: number;
}) => {
  if (typeof value === "string" && value === "") {
    return false;
  }
  return Number(value) <= comparator;
};

export const $gt = ({
  value,
  comparator,
}: {
  value: number | string;
  comparator: number;
}) => {
  if (typeof value === "string" && value === "") {
    return false;
  }
  return Number(value) > comparator;
};

export const $gte = ({
  value,
  comparator,
}: {
  value: number | string;
  comparator: number;
}) => {
  if (typeof value === "string" && value === "") {
    return false;
  }
  return Number(value) >= comparator;
};

export const ConditionFunctions = {
  $in,
  $eq,
  $lt,
  $lte,
  $gt,
  $gte,
};

export type ConditionFunctionTypes = keyof typeof ConditionFunctions;

export const ConditionOperators = {
  $IN: "$in",
  $EQ: "$eq",
  $LT: "$lt",
  $LTE: "$lte",
  $GT: "$gt",
  $GTE: "$gte",
  $LIKE: "$like",
  $EXISTS: "$exists",
} as const;
export type ConditionOperatorsType =
  (typeof ConditionOperators)[keyof typeof ConditionOperators];

export const DateTimeSubstitutions = {
  $NOW: "$now",
};

type ConditionStatement =
  | FormSchemaQuestionAnswerType
  | Date
  | Record<ConditionFunctionTypes, FormSchemaQuestionAnswerType | Date>;

export type QuestionConditionType = Record<string, ConditionStatement>;

type FormQuestionConditions =
  | QuestionConditionType
  | { $or: QuestionConditionType[] }
  | object;

const isQuestionMatched = ({
  values,
  fieldName,
  key,
  value,
}: {
  values: { answers: FormSubmissionAnswersInFormType };
  fieldName: string;
  key: string;
  value: ConditionStatement;
}) => {
  const parentFieldName = getParentQuestionName({
    currentQuestionId: fieldName,
    formValues: values,
    questionIdToFind: key,
  });
  return !isPlainObject(value)
    ? $eq({
        value: value as unknown as any,
        comparator: get(values, getValuePathFromQuestionPath(parentFieldName)),
      })
    : Object.entries(value).every(([condition, comparator]) => {
        return ConditionFunctions?.[condition as ConditionFunctionTypes]({
          value: get(
            values,
            getValuePathFromQuestionPath(parentFieldName)
          ) as unknown as any,
          comparator,
        });
      });
};

export const areConditionsSatisfied = ({
  conditions,
  values,
  fieldName,
}: {
  conditions?: FormQuestionConditions | string;
  values: { answers: FormSubmissionAnswersInFormType };
  fieldName: string;
}) => {
  if (!conditions) {
    return true;
  }

  let formattedConditions: FormQuestionConditions;

  if (typeof conditions === "string") {
    formattedConditions = JSON.parse(conditions);
  } else {
    formattedConditions = conditions;
  }

  return Object.entries(formattedConditions).every(
    ([key, questionConditions]) => {
      if (key === "$or")
        return questionConditions.find(
          (questionCondition: QuestionConditionType) =>
            Object.entries(questionCondition).every(
              ([questionToFind, conditionStatement]) =>
                isQuestionMatched({
                  values,
                  fieldName,
                  key: questionToFind,
                  value: conditionStatement,
                })
            )
        );
      else
        return isQuestionMatched({
          values,
          fieldName,
          key,
          value: questionConditions,
        });
    }
  );
};

/**
 * Resolves a question's configuration by applying any active conditional overrides.
 * This ensures that properties like measurement_unit and measurement_type reflect
 * the correct values based on the current form submission's answers.
 *
 * @param questionId - The ID of the question to resolve
 * @param sectionId - The ID of the section containing the question
 * @param sectionIdx - The index of the section instance (for repeatable sections)
 * @param formSchema - The form schema
 * @param allAnswers - All answers from the form submission
 * @returns The resolved question configuration with any applicable overrides applied
 */
export const resolveQuestionConfiguration = (
  questionId: string,
  sectionId: string,
  sectionIdx: number,
  formSchema: FormSchemaType,
  allAnswers: FormSubmissionAnswersBySectionMapType
): FormSchemaType["config"]["questions"][string] => {
  const question = formSchema.config.questions[questionId];

  // If there are no conditional overrides, return the base question config
  if (!question?.conditional_overrides?.length) {
    return question;
  }

  const fieldPath = `answers.${sectionId}.${sectionIdx}.${questionId}`;

  // Find the first matching override (first match wins)
  for (const override of question.conditional_overrides) {
    const conditionsSatisfied = areConditionsSatisfied({
      conditions: override.conditions,
      values: { answers: allAnswers },
      fieldName: fieldPath,
    });

    if (conditionsSatisfied) {
      // Apply the override properties to a copy of the question
      return {
        ...question,
        ...(override.equation !== undefined && { equation: override.equation }),
        ...(override.measurement_type !== undefined && {
          measurement_type: override.measurement_type,
        }),
        ...(override.measurement_unit !== undefined && {
          measurement_unit: override.measurement_unit,
        }),
        ...(override.minimum !== undefined && { minimum: override.minimum }),
        ...(override.maximum !== undefined && { maximum: override.maximum }),
        ...(override.pick_list_values !== undefined && {
          pick_list_values: override.pick_list_values,
        }),
        ...(override.before !== undefined && { before: override.before }),
        ...(override.after !== undefined && { after: override.after }),
      };
    }
  }

  // No matching override found, return the base question config
  return question;
};

export const getParentQuestionName = ({
  currentQuestionId,
  formValues,
  questionIdToFind,
}: {
  currentQuestionId: string;
  formValues: Record<string, unknown>;
  questionIdToFind: string;
}) => {
  if (currentQuestionId && questionIdToFind && formValues) {
    /**
     * Split a question name from "answers.sectionOne.0.question1"
     * to: ["answers", sectionId, sectionIndex, questionId]
     */
    const [_, sectionId, sectionIndex, __] =
      currentQuestionId?.split(".") ?? [];

    // Try finding a matching parent from the same section:
    const value = get(
      formValues,
      `answers.${sectionId}.${sectionIndex}.${questionIdToFind}`
    );

    if (value) {
      return `answers.${sectionId}.${sectionIndex}.${questionIdToFind}`;
    }

    // If parent is not on the same section,
    // try finding a matching parent by searching all questions
    // using questionId that's not in the same section:
    const fieldName = Object.entries(formValues?.answers ?? {}).find(
      ([key, objectValue]) =>
        key !== sectionId && objectValue?.[0]?.[questionIdToFind]
    )?.[0];

    return `answers.${fieldName}.0.${questionIdToFind}`;
  }
  return "";
};

export const getSmartDefaultValues = (
  formSchema?: FormSchemaType,
  contextForDefaults: {
    now?: string;
    currentUserName?: string;
    associatedAssetId?: string;
    associatedAssetType?: AssetTypeType;
    defaultValues?: Record<string, string>;
  } = { defaultValues: {} }
) => {
  if (!formSchema) return {};

  return {
    answers: formSchema?.config?.sections.reduce(
      (
        total: Record<string, Array<Record<string, FormSubmissionAnswerType>>>,
        { id, questions }: FormSchemaSectionType
      ) => ({
        ...total,
        [id]: [
          questions.reduce<Record<string, FormSubmissionAnswerType>>(
            (questionDefaults, qid) => {
              const question = formSchema?.config?.questions[qid];

              if (!question) return questionDefaults;

              let defaultAnswer =
                contextForDefaults?.defaultValues?.[
                  `$.questions.${qid}.default_answer`
                ] ??
                question?.default_answer ??
                "";

              /**
               * If the default answer type is a lookup reference, don't use it
               * as it will be resolved later
               */
              if (
                isObject(defaultAnswer) &&
                "lookup_attribute" in defaultAnswer &&
                "lookup_question_id" in defaultAnswer
              ) {
                defaultAnswer = "";
              }

              questionDefaults[qid] = {
                value: defaultAnswer,
              };

              switch (question.type) {
                case FormSchemaQuestionType.QUESTION:
                  switch (question.data_type) {
                    // Workflow variables may return string values for boolean questions, so we need to coerce them to booleans
                    case AttributeDataType.BOOLEAN: {
                      const rawValue = questionDefaults[qid]?.value;
                      if (
                        typeof rawValue === "string" &&
                        (rawValue === "true" || rawValue === "false")
                      ) {
                        questionDefaults[qid] = {
                          value: rawValue === "true",
                        };
                      }
                      break;
                    }
                    case AttributeDataType.DATE_TIME:
                    case AttributeDataType.DATE: {
                      if (
                        questionDefaults[qid]?.value ===
                          DateTimeSubstitutions.$NOW &&
                        contextForDefaults.now
                      )
                        questionDefaults[qid] = {
                          value: contextForDefaults.now,
                        };
                      break;
                    }
                    case AttributeDataType.STRING: {
                      if (
                        questionDefaults[qid]?.value === "$user_name" &&
                        contextForDefaults.currentUserName
                      )
                        questionDefaults[qid] = {
                          value: contextForDefaults.currentUserName,
                        };
                      break;
                    }
                    case AttributeDataType.LOOKUP: {
                      const hasExplicitDefaultAnswer =
                        questionDefaults[qid]?.value !== "";

                      if (
                        hasExplicitDefaultAnswer ||
                        !contextForDefaults.associatedAssetId ||
                        !contextForDefaults.associatedAssetType ||
                        contextForDefaults.associatedAssetType !==
                          question.lookup_entity_type
                      )
                        break;

                      questionDefaults[qid] = {
                        value: contextForDefaults.associatedAssetId,
                      };
                      break;
                    }
                    case AttributeDataType.FILE: {
                      const parsedResult = FileUploadedSchema.safeParse(
                        questionDefaults[qid].value
                      );
                      if (parsedResult.success) {
                        questionDefaults[qid] = {
                          value: [parsedResult.data] as FileUploadedType[],
                        };
                      } else {
                        questionDefaults[qid] = {
                          value: [],
                        };
                      }
                      break;
                    }
                  }
              }
              /** Prevent sending a bunch of unset fields to the backend */
              if (questionDefaults[qid]?.value === "") {
                delete questionDefaults[qid];
              }
              return questionDefaults;
            },
            {}
          ),
        ],
      }),
      {}
    ),
  };
};

/**
 * Takes the results from `getSmartDefaultValues` and removes any answers that
 * are not visible based on the conditions of the form.
 * Works as follows:
 * - Creates a copy of the calculated default answers
 * - Deletes any questions that are not visible based on the conditions (initial form values are empty)
 * - Deletes any sections with no questions
 * @param calculatedDefaultAnswers the results from `getSmartDefaultValues`
 */
export const getDefaultValuesFromCalculatedAnswers = (
  calculatedDefaultAnswers: { answers: FormSubmissionAnswersInFormType },
  formSchema: FormSchemaType
) => {
  const values: typeof calculatedDefaultAnswers = structuredClone(
    calculatedDefaultAnswers
  );
  for (const sectionId of Object.keys(calculatedDefaultAnswers.answers)) {
    for (const [sectionIdx, section] of calculatedDefaultAnswers.answers[
      sectionId
    ].entries()) {
      for (const questionId of Object.keys(section)) {
        if (
          !areConditionsSatisfied({
            fieldName: getValuePathFromQuestionPath(
              `answers.${sectionId}.${sectionIdx}.${questionId}`
            ),
            values: { answers: {} },
            conditions: formSchema?.config.questions?.[questionId]
              ?.conditions as FormQuestionConditions | string | undefined,
          })
        ) {
          delete values.answers[sectionId][sectionIdx][questionId];
        }
      }
      if (!Object.keys(values.answers[sectionId][sectionIdx]).length) {
        set(values, `answers.${sectionId}`, [
          ...values.answers[sectionId].slice(0, sectionIdx),
          ...values.answers[sectionId].slice(sectionIdx + 1),
        ]);
      }
    }
  }
  return values;
};

export const getErrorCount = (
  form: UseFormReturn<Pick<FormSubmissionType, "answers">>
): number =>
  Object.values(form.formState?.errors?.answers ?? {}).reduce(
    (total, value) => {
      if (!Array.isArray(value)) return total;
      const newErrors = value.map((value) => Object.keys(value ?? {})).flat();
      return (total += newErrors.length);
    },
    0
  );

/**
 * Takes a question path in the form of:
 * "answers.<sectionId>.<sectionIndex>.<questionId>"
 * and returns the same path with ".value" appended:
 * "answers.<sectionId>.<sectionIndex>.<questionId>.value"
 */
export const getValuePathFromQuestionPath = (questionPath: string): string =>
  `${questionPath}.value`;

/**
 * Finds a section in a form submission that matches a section in a form schema using the section's name.
 * Useful for when a section has its ID changed in the form schema, and therefore no longer match a section in a form submission.
 * @param formSchema the current schema of the form
 * @param formSubmission the form submission that potentially contains sections with mismatched IDs
 * @param sectionIdToFind the section ID to find a match for
 * @returns the section
 */
export const findSectionUsingName = (
  formSchema: FormSchemaType,
  formSubmission: FormSubmissionType,
  sectionIdToFind: string
) => {
  // Find the name of the section in the form schema for the specified section ID
  const matchingSectionName = formSchema.config.sections.find(
    (section) => section.id === sectionIdToFind
  )?.name;

  // Find the matching section in the form submission using the name of the section
  const matchingSubmissionSection =
    formSubmission.form_schema?.config.sections.find(
      (section) => section.name === matchingSectionName
    );

  return matchingSubmissionSection;
};

/**
 * Find the answer for a given form question in a form submission's answers data
 * @param allAnswers the form submission's `answers` object
 * @param questionId the question ID to find
 * @param sectionId the section ID that the question actually belongs in
 * @param sectionIdx the section instance (when repeatable can be greater than 0) to find question in first
 * @param formSchema the schema of the form
 * @param submissionSchema the schema of the submission
 * @returns the answer if found, null otherwise
 */
export const findAnswerForQuestion = (
  allAnswers: FormSubmissionAnswersBySectionMapType,
  questionId: string,
  sectionId: string,
  sectionIdx: number,
  providedOpts: {
    /** don't search in repeated sections? */
    ignoreRepeatedSections?: boolean;
    /** if the question cannot be found by ID, should we fall back to its prompt? */
    findFallbackByPrompt?: boolean;
  },
  formSchema?: FormSchemaType,
  submissionSchema?: FormSchemaType
) => {
  const opts = Object.assign({ ignoreRepeatedSections: true }, providedOpts);
  // look first in the specified section for the question's answer
  let sourceAnswer = allAnswers[sectionId][sectionIdx]?.[questionId];

  if (sourceAnswer) return sourceAnswer;

  // Check if we can fall back to the question prompt to search for an answer if we cannot find one with the current question ID.
  // E.g. If the question ID has been changed.
  if (opts.findFallbackByPrompt && formSchema && submissionSchema) {
    const question = formSchema.config.questions[questionId];
    // Find the prompt in the schema that matches the question in the submission
    const matchingSchemaQuestionConfig = Object.entries(
      formSchema.config.questions
    ).find(
      ([_, configQuestion]) => configQuestion.prompt === question.prompt
    )?.[1];
    const matchingQuestionPrompt = matchingSchemaQuestionConfig?.prompt;

    // Get the ID of the question in the submission that matches the prompt
    const matchingSubmissionQuestionId = Object.entries(
      submissionSchema.config.questions ?? {}
    ).find(([, configQuestion]) => {
      return configQuestion.prompt === matchingQuestionPrompt;
    })?.[0];

    // Set the answer to that of the matching question
    if (matchingSubmissionQuestionId) {
      sourceAnswer =
        allAnswers[sectionId][sectionIdx]?.[matchingSubmissionQuestionId];
      if (sourceAnswer) return sourceAnswer;
    }
  }

  // not found in the same section so look across other sections...
  for (const sectionIdToCheck of Object.keys(allAnswers)) {
    if (sectionId === sectionIdToCheck) {
      // Ignore other possible duplicate sections of the same section ID as the measurement.
      continue;
    }

    if (
      opts.ignoreRepeatedSections &&
      allAnswers[sectionIdToCheck]?.length !== 1
    ) {
      // sections that are repeated are ignored
      continue;
    }

    // when section is not repeated, we just want the first instance (which is
    // the only instance) if ignoreRepeatedSections if off, we still use only
    // the first instance. this is a limitation.
    sourceAnswer = allAnswers[sectionIdToCheck]?.[0]?.[questionId];

    if (sourceAnswer) return sourceAnswer;
  }

  return null;
};

/**
 * Recursively initializes form fields as dirty with their initial values.
 * This is useful when you want to mark a form with initial data as dirty from the start,
 * typically when loading existing data that should be treated as if the user has already modified it.
 *
 * @template T - Type extending FieldValues from react-hook-form
 * @param {T} values - The values object to initialize in the form
 * @param {UseFormSetValue<T>} setValue - The setValue function from react-hook-form's useForm hook
 * @param {string} [prefix=''] - The prefix for nested object paths (used for recursion)
 *
 * @example
 * ```typescript
 * const { setValue } = useForm();
 * const initialData = { name: 'John', address: { city: 'New York' } };
 *
 * // Mark all fields in initialData as dirty
 * initializeFormDirty(initialData, setValue);
 * ```
 */
export const initializeFormDirty = <T extends FieldValues>(
  values: T,
  setValue: UseFormSetValue<T>,
  prefix = ""
) => {
  if (Object.keys(values).length === 0 && prefix !== "") {
    setValue(prefix as Path<T>, {} as PathValue<T, Path<T>>, {
      shouldDirty: true,
    });
  } else {
    Object.keys(values).forEach((key) => {
      const fieldPath = prefix ? `${prefix}.${key}` : key;
      const value = values[key];
      if (Array.isArray(value)) {
        if (value.length === 0) {
          setValue(fieldPath as Path<T>, [] as PathValue<T, Path<T>>, {
            shouldDirty: true,
          });
        } else {
          value.forEach((_, index) => {
            initializeFormDirty(
              value[index],
              setValue,
              `${fieldPath}.${index}`
            );
          });
        }
      } else if (typeof value === "object" && value !== null) {
        initializeFormDirty(value, setValue, fieldPath);
      } else if (value !== null) {
        setValue(fieldPath as Path<T>, value, {
          shouldDirty: true,
        });
      }
    });
  }
};

/**
 * Get the submittable answers for a form submission from a payload of input
 * values
 * @param allAnswers input values from a form in the shape of the answers
 * payload for a submission
 * @param formSchema the form schema
 * @returns answers in the shape that the form submission endpoint expects the
 * answers to be
 */
export const getAnswersForSubmission = (
  allAnswers: FormSubmissionAnswersBySectionMapType,
  formSchema: FormSchemaType
): FormSubmissionAnswersBySectionMapType => {
  if (!allAnswers || !isPlainObject(allAnswers)) return {};

  return Object.fromEntries(
    Object.entries(allAnswers).map(([sectionId, sections]) => [
      sectionId,
      sections.map((sectionQuestions, sectionIdx) => {
        return Object.fromEntries(
          Object.entries(sectionQuestions).map(
            ([questionId, questionValue]) => {
              const questionSchema = formSchema.config.questions[questionId];

              if (!questionSchema) {
                return [questionId, questionValue];
              }

              //Don't include questions that are not visible based on their conditions
              const valuePath = getValuePathFromQuestionPath(
                `answers.${sectionId}.${sectionIdx}.${questionId}`
              );
              if (
                questionSchema.conditions &&
                !areConditionsSatisfied({
                  fieldName: valuePath,
                  values: { answers: allAnswers },
                  conditions: questionSchema.conditions,
                })
              ) {
                return [];
              }

              switch (questionSchema.type) {
                case FormSchemaQuestionType.QUESTION: {
                  switch (questionSchema.data_type) {
                    case AttributeDataType.FILE: {
                      if (!questionValue) {
                        return [questionId, { value: null }];
                      }

                      if (!Array.isArray(questionValue?.value)) {
                        return FileUploadedSchema.safeParse(questionValue.value)
                          .success
                          ? [questionId, questionValue]
                          : [questionId, { value: null }];
                      }

                      if (!questionValue.value.length) {
                        return [questionId, { value: null }];
                      }

                      return [questionId, { value: questionValue.value.at(0) }];
                    }
                    case AttributeDataType.GEO_POINT: {
                      if (
                        !questionSchema.is_required &&
                        Array.isArray(questionValue.value) &&
                        questionValue.value[0] === "" &&
                        questionValue.value[1] === ""
                      ) {
                        // Remove empty optional geopoint answer from being submitted
                        return [];
                      }
                      return [questionId, questionValue];
                    }
                    default:
                      return [questionId, questionValue];
                  }
                }
                default:
                  return [questionId, questionValue];
              }
            }
          )
        ) as FormSubmissionAnswersByQuestionMapType;
      }),
    ])
  );
};

/**
 * Converts a question value to a value that can be used in a question input component
 * @param dataType the data type of the question
 * @param value the value of the question
 * @returns the value that can be used in a question input component
 */
export const getValueForQuestionInputFromQuestionValue = (
  dataType: AttributeDataTypeType,
  value: FormSchemaQuestionAnswerType
): any => {
  switch (dataType) {
    // if it is a plain date there is no timezone
    // read it back in the local timezone so the date stays the same as it was serialized
    // if precision is required the underlying type must be a datetime and not a date
    case AttributeDataType.DATE: {
      const local = new Date(value + "T00:00:00");
      return isValidDate(local) ? local : value;
    }
    case AttributeDataType.DATE_TIME: {
      const parseResult = DateTimeValueSchemas.dateTime.safeParse(value);

      if (!parseResult.success) return value;

      const date = new Date(parseResult.data);
      return isValidDate(date) ? date : value;
    }
    case AttributeDataType.DATE_TIME_RANGE: {
      const parseResult =
        DateTimeValueSchemas.dateTimeRangeSimple.safeParse(value);

      if (!parseResult.success) return value;

      const dates = parseResult.data.map((v) => {
        const parseResult = DateTimeValueSchemas.dateTime.safeParse(v);

        if (!parseResult.success) return v;

        return new Date(parseResult.data);
      });
      return dates.some((date) => !isValidDate(date)) ? value : dates;
    }
    case AttributeDataType.FILE: {
      const parseResult = FileUploadedSchema.safeParse(value);

      if (parseResult.success) {
        return [parseResult.data];
      } else {
        return [];
      }
    }
    default:
      return value;
  }
};

/**
 * Retrieves all unique pick list values from a question, including both default values
 * and values from conditional overrides.
 *
 * This function combines the base pick list values with any pick list values defined in
 * conditional overrides, then deduplicates them to return a complete set of all possible
 * pick list values for the question.
 *
 * @param question - The form question to extract pick list values from
 * @returns An array of unique pick list values. Returns an empty array if the question
 *          is not a PICK_LIST or MULTI_PICK_LIST type.
 */
export const getPickListValuesWithConditionalOverrides = (
  question: FormSchemaNormalQuestionType
) => {
  if (
    question.data_type !== AttributeDataType.PICK_LIST &&
    question.data_type !== AttributeDataType.MULTI_PICK_LIST
  ) {
    return [];
  }

  const defaultPickListValues = question.pick_list_values ?? [];
  const conditionalOverridesPickListValues =
    question.conditional_overrides?.flatMap(
      (override) => override?.pick_list_values ?? []
    ) ?? [];

  return Array.from(
    new Set([...defaultPickListValues, ...conditionalOverridesPickListValues])
  );
};

/**
 * Extracts question IDs from conditional override conditions
 */
export const extractQuestionIdsFromConditions = (
  conditions: FormQuestionConditions
): Set<string> => {
  const questionIds = new Set<string>();
  if (!conditions) return questionIds;

  Object.entries(conditions).forEach(([key, questionConditions]) => {
    if (key === "$or") {
      if (Array.isArray(questionConditions)) {
        questionConditions.forEach((orCondition: QuestionConditionType) => {
          Object.keys(orCondition).forEach((questionId) => {
            questionIds.add(questionId);
          });
        });
      }
    } else {
      questionIds.add(key);
    }
  });

  return questionIds;
};

/**
 * Checks if any of the questions referenced in conditions have changed
 * Uses getParentQuestionName to find where source questions are located (same approach as areConditionsSatisfied)
 */
export const checkIfAnswersToSourceQuestionsChanged = (
  conditions: FormQuestionConditions,
  previousFormValues: { answers: FormSubmissionAnswersInFormType } | undefined,
  currentFormValues: { answers: FormSubmissionAnswersInFormType },
  currentFieldPath: string
): boolean => {
  if (!previousFormValues) return false;

  const sourceQuestionIds = extractQuestionIdsFromConditions(conditions);

  return Array.from(sourceQuestionIds).some((sourceQuestionId) => {
    const sourceQuestionPath = getParentQuestionName({
      currentQuestionId: currentFieldPath,
      formValues: currentFormValues,
      questionIdToFind: sourceQuestionId,
    });

    if (!sourceQuestionPath) return false;

    const previousValue = get(
      previousFormValues,
      getValuePathFromQuestionPath(sourceQuestionPath)
    );
    const currentValue = get(
      currentFormValues,
      getValuePathFromQuestionPath(sourceQuestionPath)
    );

    return !isEqual(currentValue, previousValue);
  });
};

/**
 * Derives which form value paths were last written by the user, given a write the form just reported.
 *
 * A user write adds its path. A write from code (e.g. a default, a lookup or a conditional override applying) hands that
 * path, and any path it contains or is contained by, back to the system. A write with no path (e.g. a form reset) hands
 * every path back.
 */
export const getNextUserChangedFieldPaths = ({
  userChangedFieldPaths,
  writtenFieldPath,
  isWrittenByUser,
}: {
  userChangedFieldPaths: ReadonlySet<string>;
  writtenFieldPath: string | undefined;
  isWrittenByUser: boolean;
}): ReadonlySet<string> => {
  if (isWrittenByUser) {
    return writtenFieldPath
      ? new Set(userChangedFieldPaths).add(writtenFieldPath)
      : userChangedFieldPaths;
  }

  if (!writtenFieldPath) return new Set();

  return new Set(
    Array.from(userChangedFieldPaths).filter(
      (userChangedFieldPath) =>
        userChangedFieldPath !== writtenFieldPath &&
        !userChangedFieldPath.startsWith(`${writtenFieldPath}.`) &&
        !writtenFieldPath.startsWith(`${userChangedFieldPath}.`)
    )
  );
};
